// See: backend/docs/driver-scheduler.md §11
//
// Driver Scheduler API'leri için React hook'u.
//
// Sprint 1:
//   - getCandidates(serviceId)
// Sprint 2:
//   - assignDriver(serviceId, driverId, opts)  → /assign-driver (audit + lock destekli)
//   - setLock(serviceId, locked)               → /lock
//   - getDailySchedule(date)                   → /schedule?date=...
// Sprint 3:
//   - optimizeDay(date, { dryRun })            → /optimize-day (toplu re-optimization)

import { useCallback } from "react";

import { useHttpClient } from "shared/hooks/http-hook";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/driver-scheduler`;

export const ASSIGNMENT_SOURCES = Object.freeze({
  MANUAL: "MANUAL",
  AUTO_INSERT: "AUTO_INSERT",
  AUTO_REOPTIMIZE: "AUTO_REOPTIMIZE",
});

export const useDriverSchedulerApi = () => {
  const { isLoading, error, sendRequest } = useHttpClient();

  const getCandidates = useCallback(
    async (serviceId, { max } = {}) => {
      if (!serviceId) return null;
      const qs = max ? `?max=${encodeURIComponent(max)}` : "";
      return sendRequest(`${BASE}/services/${serviceId}/candidates${qs}`);
    },
    [sendRequest],
  );

  /**
   * Bir adayı seçip atamayı uygula.
   * @param {string} serviceId
   * @param {string} driverId
   * @param {Object} opts
   * @param {string} [opts.source]   - "MANUAL" | "AUTO_INSERT" | "AUTO_REOPTIMIZE"
   * @param {boolean} [opts.lock]    - true ise atamayı kilitle
   * @param {number}  [opts.cost]    - Aday skoru (debug için)
   */
  const assignDriver = useCallback(
    async (serviceId, driverId, opts = {}) => {
      if (!serviceId || !driverId) return null;
      const body = {
        driverId,
        source: opts.source || ASSIGNMENT_SOURCES.AUTO_INSERT,
        lock: !!opts.lock,
        ...(typeof opts.cost === "number" ? { cost: opts.cost } : {}),
      };
      const responseData = await sendRequest(
        `${BASE}/services/${serviceId}/assign-driver`,
        "POST",
        JSON.stringify(body),
      );
      return responseData?.personelservice || null;
    },
    [sendRequest],
  );

  /**
   * Bir servisin kilidini aç/kapat.
   */
  const setLock = useCallback(
    async (serviceId, locked) => {
      if (!serviceId) return null;
      const responseData = await sendRequest(
        `${BASE}/services/${serviceId}/lock`,
        "PATCH",
        JSON.stringify({ locked: !!locked }),
      );
      return responseData?.personelservice || null;
    },
    [sendRequest],
  );

  /**
   * Belirli bir tarih için günlük Gantt verisi.
   * @param {string} date - "YYYY-MM-DD"
   */
  const getDailySchedule = useCallback(
    async (date) => {
      if (!date) return null;
      return sendRequest(`${BASE}/schedule?date=${encodeURIComponent(date)}`);
    },
    [sendRequest],
  );

  /**
   * Belirli bir tarih için toplu re-optimization.
   * @param {string} date - "YYYY-MM-DD"
   * @param {Object} opts
   * @param {boolean} [opts.dryRun=true] - false ise gerçek atamaları uygular
   * @param {string}  [opts.scope] - "TRANSFER_AND_SHIFT" (varsayılan)
   */
  const optimizeDay = useCallback(
    async (date, opts = {}) => {
      if (!date) return null;
      const body = {
        date,
        dryRun: opts.dryRun === false ? false : true,
        scope: opts.scope || "TRANSFER_AND_SHIFT",
      };
      return sendRequest(`${BASE}/optimize-day`, "POST", JSON.stringify(body));
    },
    [sendRequest],
  );

  /**
   * Tüm aktif sürücülerin izin listesi (weeklyOffDays + optOutDates).
   */
  const listAvailability = useCallback(async () => {
    return sendRequest(`${BASE}/availability`);
  }, [sendRequest]);

  /**
   * Belirli bir tarihteki izinli/müsait sürücü ayrımı.
   * @param {string} date - "YYYY-MM-DD"
   */
  const getAvailabilityForDate = useCallback(
    async (date) => {
      if (!date) return null;
      return sendRequest(`${BASE}/availability?date=${encodeURIComponent(date)}`);
    },
    [sendRequest],
  );

  /**
   * Bir sürücünün izin alanlarını günceller (partial — sadece gönderilen alan yazılır).
   * @param {string} driverId
   * @param {Object} patch
   * @param {number[]} [patch.weeklyOffDays] - 1=Pazartesi ... 7=Pazar
   * @param {string[]} [patch.optOutDates]   - ["YYYY-MM-DD", ...]
   */
  const updateAvailability = useCallback(
    async (driverId, patch = {}) => {
      if (!driverId) return null;
      const body = {};
      if (Array.isArray(patch.weeklyOffDays)) body.weeklyOffDays = patch.weeklyOffDays;
      if (Array.isArray(patch.optOutDates)) body.optOutDates = patch.optOutDates;
      const responseData = await sendRequest(
        `${BASE}/availability/${driverId}`,
        "PATCH",
        JSON.stringify(body),
      );
      return responseData?.availability || null;
    },
    [sendRequest],
  );

  return {
    isLoading,
    error,
    getCandidates,
    assignDriver,
    setLock,
    getDailySchedule,
    optimizeDay,
    listAvailability,
    getAvailabilityForDate,
    updateAvailability,
  };
};

export default useDriverSchedulerApi;
